/**
 * AnalyticsDashboard — internal admin view of app analytics.
 * Only visible/usable by admin users (enforced server-side too).
 * Route: /admin/analytics
 */
import { useEffect, useState } from "react";
import api from "../../services/api";

const PERIODS = [
  { label: "Last 7 days",  value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
];

export default function AnalyticsDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [days, setDays]       = useState(30);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/app-analytics/admin?days=${days}`)
      .then(({ data: res }) => {
        if (res.success) setData(res.data);
        else setError(res.error || "Failed to load");
      })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="p-8 text-slate-400">Loading analytics…</div>;
  if (error)   return <div className="p-8 text-red-400">Error: {error}</div>;

  const { totals, top_pages, top_events, utm_sources, daily_views, recent_events } = data;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">App Analytics</h1>
        <div className="flex gap-2">
          {PERIODS.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setDays(value)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                days === value
                  ? "bg-blue-600 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Page Views",    value: totals.page_views },
          { label: "Sessions",      value: totals.unique_sessions },
          { label: "Logged-in",     value: totals.logged_in_users },
          { label: "Registrations", value: totals.registrations },
          { label: "Total Events",  value: totals.total_events },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-800 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-white">{Number(value).toLocaleString()}</div>
            <div className="text-sm text-slate-400 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Section title="Top Pages">
          <Table
            rows={top_pages}
            columns={[
              { key: "page",  label: "Page" },
              { key: "views", label: "Views", align: "right" },
            ]}
          />
        </Section>

        {/* Top Events */}
        <Section title="Custom Events">
          <Table
            rows={top_events}
            columns={[
              { key: "event_name", label: "Event" },
              { key: "count",      label: "Count", align: "right" },
            ]}
          />
        </Section>

        {/* UTM Sources */}
        <Section title="Traffic Sources (UTM)">
          <Table
            rows={utm_sources}
            columns={[
              { key: "utm_source",   label: "Source" },
              { key: "utm_medium",   label: "Medium" },
              { key: "utm_campaign", label: "Campaign" },
              { key: "visits",       label: "Visits", align: "right" },
            ]}
          />
        </Section>

        {/* Daily page views sparkline (simple) */}
        <Section title="Daily Page Views">
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {daily_views.length === 0 && (
              <p className="text-slate-500 text-sm">No data</p>
            )}
            {daily_views.map(({ date, views }) => {
              const maxViews = Math.max(...daily_views.map((d) => Number(d.views)), 1);
              const pct = (Number(views) / maxViews) * 100;
              return (
                <div key={date} className="flex items-center gap-3 text-sm">
                  <span className="text-slate-500 w-24 shrink-0">{date}</span>
                  <div className="flex-1 bg-slate-700 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-slate-300 w-8 text-right">{views}</span>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      {/* Recent Events */}
      <Section title="Recent Events (last 50)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 text-left border-b border-slate-700">
                {["Time", "Event", "Page", "Session", "UTM Source", "Properties"].map((h) => (
                  <th key={h} className="pb-2 pr-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent_events.map((ev) => (
                <tr key={ev.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                  <td className="py-2 pr-4 text-slate-500 whitespace-nowrap">
                    {new Date(ev.created_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-blue-400 font-medium">{ev.event_name}</td>
                  <td className="py-2 pr-4 text-slate-300">{ev.page || "—"}</td>
                  <td className="py-2 pr-4 text-slate-500 font-mono text-xs">
                    {ev.session_id?.slice(0, 8)}…
                  </td>
                  <td className="py-2 pr-4 text-slate-400">{ev.utm_source || "—"}</td>
                  <td className="py-2 pr-4 text-slate-500 text-xs">
                    {ev.properties ? JSON.stringify(ev.properties) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-slate-800 rounded-lg p-5">
      <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Table({ rows, columns }) {
  if (!rows?.length) return <p className="text-slate-500 text-sm">No data</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-slate-400 text-left border-b border-slate-700">
          {columns.map((col) => (
            <th key={col.key} className={`pb-2 font-medium ${col.align === "right" ? "text-right" : ""}`}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
            {columns.map((col) => (
              <td
                key={col.key}
                className={`py-2 pr-2 text-slate-300 ${col.align === "right" ? "text-right" : ""}`}
              >
                {row[col.key] ?? "—"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
